import React, { useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { colors, fontSize } from "style";
import Card from "components/Card";
import { PrimaryButton } from "components/Buttons";
import { useVideoPresentation } from "hooks";
import { InterviewTitle, InterviewSubtitle } from "components/VideoPresentation";

/**
 * PermissionStep Component
 *
 * @param {Function} onGranted
 * @param {Function} onBack
 * @param {Boolean}  showBack
 */
const PermissionStep = ({ onGranted, showBack, onBack }) => {
  const { getMediaConstraints } = useVideoPresentation();
  const [requesting, setRequesting] = useState(false);

  const handleAskPermission = async () => {
    setRequesting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(getMediaConstraints(null, null));
      onGranted(stream);
    } catch (err) {
      alert("We need access to your camera and microphone to continue. Please allow access and try again.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div css={styles.container}>
      <InterviewTitle titleId="VideoPresentation.PermissionStep.Title" />
      <InterviewSubtitle subtitleId="VideoPresentation.PermissionStep.Subtitle" />
      <Card css={styles.card}>
        <div css={styles.badge}>
          <FormattedMessage id="VideoPresentation.PermissionStep.Badge" />
        </div>

        <p css={styles.text}>
          <FormattedMessage id="VideoPresentation.PermissionStep.Description" />
        </p>

        <div css={styles.button_row}>
          {showBack && (
            <PrimaryButton variant="secondary" onClick={onBack}>
              <FormattedMessage id="VideoPresentation.PermissionStep.Back" />
            </PrimaryButton>
          )}

          <PrimaryButton onClick={handleAskPermission} disabled={requesting}>
            {requesting ? (
              <FormattedMessage id="VideoPresentation.PermissionStep.Requesting" />
            ) : (
              <FormattedMessage id="VideoPresentation.PermissionStep.AllowAccess" />
            )}
          </PrimaryButton>
        </div>
      </Card>
    </div>
  );
};

const styles = {
  container: css`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  card: css`
    padding: 24px;
    border-radius: 20px;
    box-shadow: 0 8px 20px rgba(17, 24, 39, 0.06);
    display: flex;
    flex-direction: column;
    gap: 20px;
  `,
  badge: css`
    padding: 4px 12px;
    border-radius: 999px;
    align-self: flex-start;
    background: ${colors.purpleRainLight3};
    border: 1px solid ${colors.purpleRainDark2};
    color: ${colors.purpleRainBase};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  `,
  text: css`
    font-size: ${fontSize.normal};
    color: ${colors.purpleRainDark4};
    margin: 0;
  `,
  button_row: css`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 8px;
  `,
};

PermissionStep.propTypes = {
  onGranted: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  showBack: PropTypes.bool,
};

export default PermissionStep;
